/** Окно отправки: канал, ветка/пост форума, бот или вебхук, правка существующего сообщения. */
import { hasErrors, validateDraft } from '../../../src/shared/validate.js';
import type { EditTarget } from '../../../src/shared/types.js';
import { ApiError, api, type ChannelInfo, type SendTarget } from '../api.js';
import { app, channelById } from '../ctx.js';
import { h, icon } from '../dom.js';
import { store } from '../store.js';
import { modal, toast } from '../ui/overlay.js';
import { segmented } from './common.js';

const FORUM = [15, 16];
const LS_KEY = 'es:lastTarget';

function loadLast(): Partial<SendTarget> {
  try {
    return JSON.parse(localStorage.getItem(`${LS_KEY}:${app.guild?.id}`) ?? '{}');
  } catch {
    return {};
  }
}
function saveLast(t: SendTarget) {
  try {
    localStorage.setItem(`${LS_KEY}:${app.guild?.id}`, JSON.stringify({ channelId: t.channelId, via: t.via }));
  } catch {
    /* приватный режим */
  }
}

function channelOptions(selected: string): HTMLSelectElement {
  const g = app.guild!;
  const sel = h('select', { class: 'input select', attrs: { 'aria-label': 'Канал' } });
  sel.append(h('option', { value: '' }, '- выберите канал -'));
  const cats = g.channels.filter((c) => c.type === 4);
  const sendable = (c: ChannelInfo) => c.type !== 4 && c.userCanSend;
  const opt = (c: ChannelInfo) =>
    h(
      'option',
      { value: c.id, selected: c.id === selected, disabled: !c.botCanSend && !c.botCanWebhook },
      `${FORUM.includes(c.type) ? '💬' : [2, 13].includes(c.type) ? '🔊' : c.type === 5 ? '📢' : '#'} ${c.name}${!c.botCanSend && !c.botCanWebhook ? ' (у бота нет доступа)' : ''}`,
    );
  const noCat = g.channels.filter((c) => !c.parentId && sendable(c));
  noCat.forEach((c) => sel.append(opt(c)));
  for (const cat of cats) {
    const inner = g.channels.filter((c) => c.parentId === cat.id && sendable(c));
    if (!inner.length) continue;
    sel.append(h('optgroup', { label: cat.name.toUpperCase() }, inner.map(opt)));
  }
  return sel;
}

export function openSendModal(onDone?: () => void) {
  const g = app.guild!;
  const d = store.draft;
  const m = modal({ title: 'Отправка сообщения', icon: 'send', subtitle: g.name });

  const editable = store.editTarget;
  let mode: 'new' | 'edit' = editable ? 'edit' : 'new';
  const last = loadLast();
  const target: SendTarget = { channelId: last.channelId && channelById(last.channelId) ? last.channelId : '', via: last.via ?? 'bot' };
  let threadMode: 'channel' | 'thread' | 'post' = 'channel';
  let editLink = '';
  let editTarget: EditTarget | null = editable;

  const content = h('div', { class: 'send-body' });
  const issuesBox = h('div');
  const sendBtn = h('button', { class: 'btn btn-primary', type: 'button' }, icon('send', 16), 'Отправить');

  const renderIssues = () => {
    const issues = validateDraft(d, { target: mode === 'edit' ? editTarget?.via : target.via, edit: mode === 'edit' && editTarget ? { wasV2: editTarget.wasV2 } : undefined });
    const errs = issues.filter((i) => i.level === 'error');
    issuesBox.replaceChildren(
      ...(errs.length
        ? [h(
            'div',
            { class: 'callout callout-error' },
            icon('alert', 16),
            h('div', null, h('strong', null, `Исправьте ${errs.length} ${errs.length === 1 ? 'ошибку' : 'ошибки'} перед отправкой`), h('ul', null, errs.slice(0, 6).map((e) => h('li', null, `${e.where}: ${e.message}`)))),
          )]
        : []),
    );
    return !hasErrors(issues);
  };

  const render = () => {
    content.replaceChildren();
    content.append(
      segmented(
        [
          { value: 'new', label: 'Новое сообщение', icon: 'plus' },
          { value: 'edit', label: 'Изменить существующее', icon: 'edit' },
        ],
        mode,
        (v) => {
          mode = v as 'new' | 'edit';
          render();
        },
        'Режим отправки',
      ),
    );

    if (mode === 'new') {
      const sel = channelOptions(target.channelId);
      sel.addEventListener('change', () => {
        target.channelId = sel.value;
        target.threadId = undefined;
        threadMode = 'channel';
        render();
      });
      content.append(h('label', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Канал')), sel));
      const ch = g.channels.find((c) => c.id === target.channelId);
      if (ch) {
        const isForum = FORUM.includes(ch.type);
        const threads = g.threads.filter((t) => t.parentId === ch.id);
        if (isForum) {
          if (threadMode === 'channel') threadMode = 'post';
          content.append(
            segmented(
              [
                { value: 'post', label: 'Новый пост' },
                { value: 'thread', label: 'В существующий пост', },
              ],
              threadMode,
              (v) => {
                threadMode = v as 'post' | 'thread';
                render();
              },
            ),
          );
          if (threadMode === 'post') {
            const nameInput = h('input', { class: 'input', value: target.threadName ?? '', maxLength: 100, placeholder: 'Название поста', attrs: { 'aria-label': 'Название поста' } });
            nameInput.addEventListener('input', () => (target.threadName = nameInput.value));
            content.append(h('label', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Название поста'), h('span', { class: 'req' }, '*')), nameInput));
            if (ch.tags.length) {
              const tagBox = h(
                'div',
                { class: 'chips' },
                ch.tags.map((t) => {
                  const on = target.tagIds?.includes(t.id);
                  const chip: HTMLButtonElement = h(
                    'button',
                    {
                      class: ['chip', on && 'active'],
                      type: 'button',
                      onclick: () => {
                        const set = new Set(target.tagIds ?? []);
                        if (set.has(t.id)) set.delete(t.id);
                        else if (set.size < 5) set.add(t.id);
                        target.tagIds = [...set];
                        chip.classList.toggle('active', set.has(t.id));
                      },
                    },
                    t.emoji ? `${t.emoji} ` : '',
                    t.name,
                  );
                  return chip;
                }),
              );
              content.append(h('div', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Теги (до 5)')), tagBox));
            }
            target.threadId = undefined;
          }
        }
        if ((isForum && threadMode === 'thread') || (!isForum && threads.length)) {
          const tsel = h(
            'select',
            { class: 'input select', attrs: { 'aria-label': 'Ветка' } },
            !isForum ? h('option', { value: '' }, 'В сам канал') : h('option', { value: '' }, '- выберите пост -'),
            threads.map((t) => h('option', { value: t.id, selected: t.id === target.threadId }, `🧵 ${t.name}`)),
          );
          tsel.addEventListener('change', () => (target.threadId = tsel.value || undefined));
          content.append(h('label', { class: 'field' }, h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, isForum ? 'Пост' : 'Ветка (необязательно)')), tsel));
          if (isForum && !threads.length) content.append(h('div', { class: 'field-hint' }, 'Активных постов нет'));
        }
        const webhookOk = ch.botCanWebhook;
        content.append(
          h(
            'div',
            { class: 'field' },
            h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Отправитель')),
            segmented(
              [
                { value: 'bot', label: `Бот ${g.bot.username}`, icon: 'sparkles' },
                { value: 'webhook', label: d.profile.username ? `Вебхук «${d.profile.username}»` : 'Вебхук', icon: 'user' },
              ],
              target.via,
              (v) => {
                target.via = v as 'bot' | 'webhook';
                render();
              },
            ),
            target.via === 'webhook' && !webhookOk
              ? h('div', { class: 'field-hint warn' }, icon('alert', 14), 'У бота нет права «Управлять вебхуками» в этом канале.')
              : target.via === 'webhook'
                ? h('div', { class: 'field-hint' }, 'Имя и аватар берутся из раздела «Профиль вебхука». Кнопки будут работать: вебхук создаёт сам бот.')
                : !ch.botCanSend
                  ? h('div', { class: 'field-hint warn' }, icon('alert', 14), 'Бот не может писать в этот канал - выберите вебхук или выдайте права.')
                  : null,
          ),
        );
      }
    } else {
      if (editTarget) {
        const ch = channelById(editTarget.channelId);
        content.append(
          h(
            'div',
            { class: 'callout' },
            icon('edit', 16),
            h(
              'div',
              null,
              h('strong', null, 'Изменения сохранятся в существующем сообщении'),
              h('div', { class: 'muted small' }, `${ch ? `#${ch.name}` : 'канал'} · через ${editTarget.via === 'bot' ? 'бота' : 'вебхук'}${editTarget.wasV2 ? ' · Components V2' : ''}`),
              h('a', { class: 'link small', href: `https://discord.com/channels/${g.id}/${editTarget.channelId}/${editTarget.messageId}`, target: '_blank', rel: 'noreferrer' }, 'Открыть в Discord'),
            ),
          ),
        );
      }
      const linkInput = h('input', { class: 'input', value: editLink, placeholder: 'https://discord.com/channels/...', attrs: { 'aria-label': 'Ссылка на сообщение' } });
      linkInput.addEventListener('input', () => (editLink = linkInput.value));
      const check = h(
        'button',
        {
          class: 'btn',
          type: 'button',
          onclick: async () => {
            check.disabled = true;
            try {
              const res = await api.loadMessage(g.id, editLink);
              if (!res.editTarget) {
                toast('Это сообщение отправлено не этим ботом - его можно только скопировать (JSON -> По ссылке).', 'error');
              } else {
                editTarget = res.editTarget;
                toast('Сообщение найдено - изменения сохранятся в нём', 'ok');
                render();
              }
            } catch (err: any) {
              toast(err.message, 'error');
            } finally {
              check.disabled = false;
            }
          },
        },
        'Проверить',
      );
      content.append(
        h(
          'div',
          { class: 'field' },
          h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, editTarget ? 'Другое сообщение по ссылке' : 'Ссылка на сообщение бота')),
          h('div', { class: 'input-group' }, linkInput, check),
          h('div', { class: 'field-hint' }, 'ПКМ по сообщению -> «Копировать ссылку». Редактировать можно сообщения этого бота и его вебхуков.'),
        ),
      );
    }
    const ok = renderIssues();
    sendBtn.disabled = !ok || (mode === 'new' ? !target.channelId : !editTarget);
    sendBtn.replaceChildren(icon(mode === 'edit' ? 'save' : 'send', 16), mode === 'edit' ? 'Сохранить изменения' : 'Отправить');
  };

  sendBtn.addEventListener('click', async () => {
    sendBtn.disabled = true;
    sendBtn.classList.add('is-loading');
    try {
      const res =
        mode === 'edit'
          ? await api.send(g.id, store.draft, undefined, editTarget!)
          : await api.send(g.id, store.draft, { ...target, threadId: target.threadId || undefined }, undefined);
      if (mode === 'new') saveLast(target);
      store.setMeta({ editTarget: res.editTarget ?? editTarget });
      toast(mode === 'edit' ? 'Сообщение обновлено' : 'Сообщение отправлено', 'ok', { label: 'Открыть', href: res.url });
      m.close();
      onDone?.();
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) {
        toast(`${err.message}: ${err.issues.filter((i) => i.level === 'error').map((i) => i.message).slice(0, 2).join('; ')}`, 'error');
      } else toast((err as Error).message, 'error');
    } finally {
      sendBtn.disabled = false;
      sendBtn.classList.remove('is-loading');
    }
  });

  render();
  m.body.append(content, issuesBox);
  m.footer.append(h('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Отмена'), sendBtn);
}
